import { useEffect, useState } from 'react';
import { AlertCircle, Database } from 'lucide-react';
import { MOUNT_ENGINE_LABELS, type RdsDatabase } from '@atlas/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { getCrmConfig } from '../lib/callables.js';

/**
 * The shared RDS instances client databases are mounted into. They are
 * provisioned by the infrastructure, which also holds their credentials, so
 * this page only shows them; CRMs are assigned to one on the CRMs tab.
 */
export function RdsDatabasesPage() {
  const { userRecord } = useAuth();

  const [databases, setDatabases] = useState<RdsDatabase[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const result = await getCrmConfig();
        setDatabases(result.data.config.databases);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load RDS databases.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!userRecord?.isAdmin) {
    return (
      <div className="p-8 text-sm text-[#73726e]">You don't have admin access.</div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-8 py-8">
      <div className="flex items-center gap-2 mb-1">
        <Database size={16} className="text-[#73726e]" />
        <h1 className="text-lg font-semibold text-[#37352f]">RDS databases</h1>
      </div>
      <p className="text-sm text-[#73726e] mb-6">
        The shared RDS instances client databases are mounted into, one per
        engine. They are managed with the infrastructure, credentials included;
        assign each CRM to one on the CRMs tab.
      </p>

      {loadError && (
        <p className="text-xs text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2 mb-4">
          {loadError}
        </p>
      )}

      {loading ? (
        <div className="text-sm text-[#aeaaa4]">Loading…</div>
      ) : (
        <div className="flex flex-col gap-3">
          {databases.map((d) => (
            <div
              key={d.id}
              className="border border-[#e9e9e7] rounded-lg p-4 flex flex-col gap-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-[#37352f]">{d.label}</span>
                <span className="text-[10px] font-mono text-[#aeaaa4]">{d.id}</span>
              </div>
              <div className="flex gap-6 text-xs text-[#73726e]">
                <span>{MOUNT_ENGINE_LABELS[d.engine]}</span>
                {d.endpoint ? (
                  <span className="font-mono text-[#37352f]">
                    {d.endpoint}:{d.port}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[#e03e3e]">
                    <AlertCircle size={12} /> Credentials secret unreadable — check the
                    deployment.
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
